/**
 * Outcome Distribution Widget (MA-ANALYTICS-OUTCOME-DASH-005)
 * 
 * Horizontal bar chart showing terminal outcome distribution from leads.
 * Color coding matches the builder exactly:
 *   qualified -> green
 *   not_qualified -> red
 *   abandoned -> amber/gray
 *   no_consent -> orange
 *   error -> dark red/slate
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Target, CheckCircle2, XCircle, LogOut, AlertCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';

const OUTCOME_CONFIG = {
  qualified: {
    label: 'Qualified',
    color: 'bg-green-500',
    textColor: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/20',
    icon: CheckCircle2,
  },
  not_qualified: {
    label: 'Not Qualified',
    color: 'bg-red-500',
    textColor: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/20',
    icon: XCircle,
  },
  abandoned: {
    label: 'Abandoned',
    color: 'bg-slate-400',
    textColor: 'text-slate-400',
    bgColor: 'bg-slate-500/10',
    borderColor: 'border-slate-500/20',
    icon: LogOut,
  },
  no_consent: {
    label: 'No Consent',
    color: 'bg-orange-500',
    textColor: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/20',
    icon: AlertCircle,
  },
  error: {
    label: 'Error',
    color: 'bg-red-800',
    textColor: 'text-red-300',
    bgColor: 'bg-red-900/10',
    borderColor: 'border-red-800/20',
    icon: AlertTriangle,
  },
};

function useOutcomeDistribution(period, funnelId, campaignId, tenantId) {
  return useQuery({
    queryKey: ['outcome-distribution', period, funnelId, campaignId, tenantId],
    queryFn: async () => {
      const params = new URLSearchParams({ period });
      if (funnelId) params.append('funnel_id', funnelId);
      if (campaignId && campaignId !== 'all') params.append('campaign_id', campaignId);
      if (tenantId) params.append('tenant_id', tenantId);
      const res = await api.get(`/funnel-analytics/outcome-distribution?${params}`);
      return res.data;
    },
    staleTime: 60000,
  });
}

function TrendIndicator({ delta, prevValue, size = 'sm' }) {
  if (delta === 0 && prevValue === 0) return null; // No previous data
  
  const isPositive = delta > 0;
  const isNegative = delta < 0;
  const isNeutral = delta === 0;
  
  const sizeClasses = size === 'lg' 
    ? 'text-sm gap-1' 
    : 'text-[11px] gap-0.5';
  
  return (
    <span
      className={cn(
        "inline-flex items-center font-medium",
        sizeClasses,
        isPositive && "text-green-400",
        isNegative && "text-red-400",
        isNeutral && "text-slate-400",
      )}
      title={`Previous period: ${prevValue}%`}
      data-testid="trend-indicator"
    >
      {isPositive ? '↑' : isNegative ? '↓' : '→'}
      <span>{isPositive ? '+' : ''}{delta}%</span>
    </span>
  );
}

function OutcomeBar({ outcome, count, percentage, prevPercentage, deltaPct, totalLeads, onClick }) {
  const config = OUTCOME_CONFIG[outcome] || OUTCOME_CONFIG.error;
  const Icon = config.icon;
  const maxWidth = Math.max(percentage, 2); // minimum 2% width for visibility

  return (
    <button
      className={cn(
        "w-full flex items-center gap-3 py-2.5 px-3 rounded-lg transition-all",
        "hover:bg-white/5 cursor-pointer group text-left"
      )}
      onClick={() => onClick?.(outcome)}
      data-testid={`outcome-bar-${outcome}`}
    >
      <Icon className={cn("h-4 w-4 flex-shrink-0", config.textColor)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-slate-200/80 group-hover:text-slate-100 transition-colors">
            {config.label}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-200/50">{count}</span>
            <span className={cn("text-sm font-semibold", config.textColor)}>
              {percentage}%
            </span>
            <TrendIndicator delta={deltaPct} prevValue={prevPercentage} />
          </div>
        </div>
        <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all duration-500", config.color)}
            style={{ width: `${maxWidth}%` }}
          />
        </div>
      </div>
    </button>
  );
}

export default function OutcomeDistribution({ 
  period = '30d', 
  funnelId, 
  campaignId, 
  tenantId, 
  onOutcomeClick,
  compact = false 
}) {
  const { data, isLoading, error } = useOutcomeDistribution(period, funnelId, campaignId, tenantId);

  const hasData = data?.total_leads > 0;

  return (
    <Card className="glass border-white/10" data-testid="outcome-distribution-widget">
      <CardHeader className={compact ? "pb-2" : undefined}>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-slate-100">
              <Target className="w-5 h-5 text-indigo-400" />
              Outcome Distribution
            </CardTitle>
            {!compact && (
              <CardDescription className="text-slate-200/60 mt-1">
                {hasData
                  ? `${data.total_leads} leads with terminal outcomes`
                  : 'Terminal outcome breakdown across leads'}
              </CardDescription>
            )}
          </div>
          {hasData && (
            <div className="flex items-center gap-2">
              <Badge
                className="bg-green-500/15 text-green-400 border-green-500/30 text-base font-bold px-3 py-1"
                data-testid="qualification-rate-badge"
              >
                {data.qualification_rate}% Qualified
              </Badge>
              {(data.prev_total_leads > 0 || data.qualification_rate_delta !== 0) && (
                <TrendIndicator
                  delta={data.qualification_rate_delta}
                  prevValue={data.prev_qualification_rate}
                  size="lg"
                />
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className={compact ? "pt-0" : undefined}>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-white/5 rounded animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-6 text-slate-400 text-sm">
            <Target className="w-8 h-8 mx-auto text-slate-600 mb-2" />
            <p className="text-slate-300 font-medium">Outcome Distribution Unavailable</p>
          </div>
        ) : !hasData ? (
          <div className="text-center py-8">
            <Target className="w-10 h-10 mx-auto text-slate-200/30 mb-3" />
            <p className="text-sm text-slate-200/50">No terminal outcome data yet</p>
            <p className="text-xs text-slate-200/30 mt-1">
              Outcomes appear after leads complete funnels with terminal nodes
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {data.outcomes
              .filter((o) => o.count > 0 || o.outcome === 'error')
              .map((o) => (
                <OutcomeBar
                  key={o.outcome}
                  outcome={o.outcome}
                  count={o.count}
                  percentage={o.percentage}
                  prevPercentage={o.prev_percentage}
                  deltaPct={o.delta_pct}
                  totalLeads={data.total_leads}
                  onClick={onOutcomeClick}
                />
              ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
